import { styled } from '@linaria/react';

import { bodyFont, fonts, layout, primitives, semantic, size, space } from '../../theme/tokens';

export const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GalleryFrame = styled.div`
  --image-frame-mobile-height: ${layout.heroImageMobileHeight};
  --image-frame-desktop-height: 310px;
  --image-frame-desktop-padding: 0px;
  --image-frame-desktop-radius: ${size.radius['radius-md']};
  position: relative;
  margin: 0 calc(-1 * ${space['space-600']});
  overflow: hidden;
  border-radius: ${size.radius['radius-lg']} ${size.radius['radius-lg']} 0 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    margin: ${space['space-600']} 0;
    border-radius: ${size.radius['radius-md']};
  }
`;

export const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${space['space-300']};
  margin: 0 calc(-1 * ${space['space-600']});
  border-bottom: ${size.stroke['stroke-default']} solid ${semantic.border.default};

  @media (min-width: ${layout.desktopBreakpoint}) {
    border-bottom: none;
  }
`;

export const CloseControl = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space['space-300']} ${space['space-500']};
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: ${semantic.bg.canvas};
  color: ${semantic.icon.primary};
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: color-mix(
      in srgb,
      ${semantic.bg.canvas} 95%,
      ${primitives.color.black['color-black-alpha-1000']}
    );
  }

  &[data-placement='floating'] {
    position: absolute;
    top: ${space['space-600']};
    right: ${space['space-600']};
    z-index: 2;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-1000']};
  padding: ${space['space-1000']} 0;

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space['space-400']} ${space['space-600']};

    &[data-has-footer='false'] {
      padding-bottom: ${space['space-1200']};
    }
  }
`;

export const MainBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-600']};
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space['space-400']};
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const ModalTitle = styled.h2`
  font-family: ${bodyFont};
  color: ${semantic.text.primary};
  line-height: ${primitives.typography.lineHeight.normal};
  margin: 0;

  &[data-title-variant='inline'] {
    font-size: ${fonts.size.base};
    font-weight: ${fonts.weight.normal};
  }

  &[data-title-variant='prominent'] {
    font-size: ${fonts.size.base};
    font-weight: ${fonts.weight.medium};
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    &[data-title-variant='prominent'] {
      font-size: ${fonts.size.lg};
    }
  }
`;

export const MetaText = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.secondary};
`;

export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${space['space-100']};
  flex-shrink: 0;
  text-align: right;
`;

export const StayPrice = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
`;

export const NightPrice = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  color: ${semantic.text.primary};
`;

export const RichText = styled.div`
  font-family: ${bodyFont};
  line-height: ${primitives.typography.lineHeight.normal};

  &[data-variant='description'] {
    font-size: ${fonts.size.base};
    color: ${semantic.text.primary};
  }

  &[data-variant='terms'] {
    font-size: ${fonts.size.xs};
    color: ${semantic.text.secondary};
  }

  & p {
    margin: 0;
  }

  & p + p {
    margin-top: ${space['space-300']};
  }
`;

export const AmenityList = styled.ul`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  color: ${semantic.text.primary};
  margin: 0;
  display: flex;
  flex-direction: column;

  &[data-variant='bulleted'] {
    list-style: disc;
    padding-left: ${space['space-600']};
    gap: ${space['space-400']};
  }

  &[data-variant='detailed'] {
    list-style: none;
    padding: 0;
  }

  &[data-variant='detailed'] li {
    display: flex;
    align-items: center;
    gap: ${space['space-300']};
    padding-block: ${space['space-600']};
    border-bottom: ${size.stroke['stroke-default']} solid ${semantic.border.default};
  }

  &[data-variant='detailed'] li:last-child {
    border-bottom: none;
  }

  &[data-variant='badges'] {
    list-style: none;
    padding: 0;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${space['space-200']};
  }
`;

export const AmenityIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${semantic.icon.primary};
`;

export const TotalDueRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space['space-400']};
  border-top: ${size.stroke['stroke-default']} solid ${semantic.border.default};
  padding-top: ${space['space-600']};
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  color: ${semantic.text.primary};
`;

export const TotalDueAmount = styled.span`
  font-weight: ${fonts.weight.medium};
`;

export const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const SectionHeading = styled.h3`
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};
  margin: 0;

  &[data-variant='list'] {
    font-size: ${fonts.size.sm};
    margin-bottom: ${space['space-200']};
  }
`;

export const FooterContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['space-300']};
  padding: ${space['space-300']} ${space['space-500']};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space['space-600']} ${space['space-400']};
  }
`;
